import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  BookOpen,
  AlertTriangle,
  GraduationCap,
  Dot,
} from "lucide-react";
import {
  card,
  accentBarSection,
  motionFadeUp,
  motionStaggerChild,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// ---------------------------------------------------------------------------
// DATE HELPERS
// ---------------------------------------------------------------------------

const toDateStr = (d) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatSelectedDate = (date) => {
  const dayName = DAY_NAMES[date.getDay()];
  const month = MONTH_NAMES[date.getMonth()];
  return `${dayName}, ${month} ${date.getDate()}`;
};

const dayNameToIndex = (name) => {
  const normalized =
    name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  return DAY_NAMES.indexOf(normalized);
};

// ---------------------------------------------------------------------------
// EVENT EXTRACTION — classes + exams only (no assignments)
// ---------------------------------------------------------------------------

const useEventMap = (courses) =>
  useMemo(() => {
    const map = {};

    const addEvent = (dateStr, event) => {
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(event);
    };

    (courses || []).forEach((course) => {
      const schedule = course.schedule;
      if (!schedule) return;

      const {
        classStartDate,
        classEndDate,
        midSemesterExamDate,
        endSemesterExamDate,
        classDaysAndTimes,
      } = schedule;

      // Class days within semester range
      if (classDaysAndTimes && classStartDate && classEndDate) {
        const start = new Date(classStartDate);
        const end = new Date(classEndDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        classDaysAndTimes.forEach(({ day, time }) => {
          const targetDayIdx = dayNameToIndex(day);
          if (targetDayIdx === -1) return;

          const cursor = new Date(start);
          const diff = (targetDayIdx - cursor.getDay() + 7) % 7;
          cursor.setDate(cursor.getDate() + diff);

          while (cursor <= end) {
            addEvent(toDateStr(cursor), {
              type: "class",
              time: time || "Scheduled",
              title: course.title,
              courseCode: course.courseCode,
            });
            cursor.setDate(cursor.getDate() + 7);
          }
        });
      }

      // Exam dates
      if (midSemesterExamDate) {
        addEvent(toDateStr(midSemesterExamDate), {
          type: "exam",
          time: "Mid-Semester Exam",
          title: `${course.title} — Mid-Semester Exam`,
          courseCode: course.courseCode,
        });
      }
      if (endSemesterExamDate) {
        addEvent(toDateStr(endSemesterExamDate), {
          type: "exam",
          time: "End-Semester Exam",
          title: `${course.title} — End-Semester Exam`,
          courseCode: course.courseCode,
        });
      }
    });

    return map;
  }, [courses]);

// ---------------------------------------------------------------------------
// TODAY AT A GLANCE — summary strip above calendar
// ---------------------------------------------------------------------------

const TodayAtAGlance = ({ todayEvents }) => {
  const classCount = todayEvents.filter((e) => e.type === "class").length;
  const examCount = todayEvents.filter((e) => e.type === "exam").length;

  return (
    <motion.div
      {...motionStaggerChild(0, 0.1)}
      className={cn(
        card,
        "p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
      )}
    >
      {/* Left — Greeting */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="h-10 w-10 bg-accent1/10 dark:bg-accent1/20 rounded-sm flex items-center justify-center flex-shrink-0">
          <Calendar className="h-5 w-5 text-accent1" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
            Today at a Glance
          </p>
          <p className="text-sm font-bold text-gray-900 dark:text-white tracking-tight mt-0.5">
            {todayEvents.length === 0
              ? "No events scheduled today"
              : `${todayEvents.length} event${todayEvents.length > 1 ? "s" : ""} today`}
          </p>
        </div>
      </div>

      {/* Right — Counters */}
      {todayEvents.length > 0 && (
        <div className="flex items-center gap-4">
          {classCount > 0 && (
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-accent1 rounded-sm" />
              <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                {classCount} Class{classCount > 1 ? "es" : ""}
              </span>
            </div>
          )}
          {examCount > 0 && (
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-red-500 rounded-sm" />
              <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                {examCount} Exam{examCount > 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// UPCOMING CLASSES STRIP — horizontal scroll of next 7 days of classes
// ---------------------------------------------------------------------------

const UpcomingClassesStrip = ({ eventMap }) => {
  const upcoming = useMemo(() => {
    const results = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Look ahead 14 days for upcoming classes
    for (let i = 1; i <= 14 && results.length < 5; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const dateStr = toDateStr(d);
      const events = eventMap[dateStr] || [];
      events.forEach((evt) => {
        if (results.length < 5) {
          results.push({ ...evt, date: d, dateStr });
        }
      });
    }
    return results;
  }, [eventMap]);

  if (upcoming.length === 0) return null;

  const formatShortDate = (d) => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
  };

  return (
    <motion.div {...motionStaggerChild(1, 0.1)}>
      <div className="mb-4">
        <h3 className="text-base font-bold text-gray-900 dark:text-white tracking-tight">
          Coming Up
        </h3>
        <div className={cn(accentBarSection, "mt-1.5 !w-8")} />
      </div>

      <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-3 pb-2">
        {upcoming.map((evt, index) => (
          <motion.div
            key={`${evt.dateStr}-${evt.courseCode}-${index}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.2 + index * 0.08 }}
            className="w-56 sm:w-64 flex-shrink-0 snap-start"
          >
            <div
              className={cn(
                "group flex flex-col h-full rounded-sm border overflow-hidden transition-all duration-200",
                evt.type === "exam"
                  ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:border-red-400"
                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-accent1/60"
              )}
            >
              {/* Top accent */}
              <div
                className={cn(
                  "h-1 w-full",
                  evt.type === "exam" ? "bg-red-500" : "bg-accent1"
                )}
              />

              <div className="p-4 flex-1">
                {/* Date badge */}
                <div
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm mb-3",
                    evt.type === "exam"
                      ? "bg-red-100 dark:bg-red-900/40"
                      : "bg-accent1/10 dark:bg-accent1/20"
                  )}
                >
                  <Calendar className={cn("h-3 w-3", evt.type === "exam" ? "text-red-600 dark:text-red-400" : "text-accent1")} />
                  <span
                    className={cn(
                      "text-xs font-bold",
                      evt.type === "exam" ? "text-red-600 dark:text-red-400" : "text-accent1"
                    )}
                  >
                    {formatShortDate(evt.date)}
                  </span>
                </div>

                <h4 className="text-sm font-bold text-gray-900 dark:text-white tracking-tight line-clamp-2 mb-1">
                  {evt.title}
                </h4>
                <div className="flex items-center gap-1.5 mt-2">
                  <Clock className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {evt.time}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2">
                <span className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
                  {evt.courseCode}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// CALENDAR GRID — fixed-size cells, bold dates
// ---------------------------------------------------------------------------

const CalendarGrid = ({
  currentMonth,
  currentYear,
  selectedDate,
  onSelectDate,
  eventMap,
}) => {
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const today = new Date();
  const todayStr = toDateStr(today);

  // Previous month trailing days
  const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();

  const grid = useMemo(() => {
    const cells = [];
    // Trailing days from previous month
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({ day: prevMonthDays - i, isCurrentMonth: false });
    }
    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, isCurrentMonth: true });
    }
    // Fill remaining cells to complete the grid (6 rows)
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      cells.push({ day: d, isCurrentMonth: false });
    }
    return cells;
  }, [firstDay, daysInMonth, prevMonthDays]);

  return (
    <div className="rounded-sm overflow-hidden border border-gray-200 dark:border-gray-700">
      {/* Day Headers */}
      <div className="grid grid-cols-7 bg-gray-900 dark:bg-gray-950">
        {DAYS_OF_WEEK.map((day) => (
          <div
            key={day}
            className="py-2.5 text-center text-xs uppercase tracking-widest font-bold text-white"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Date Cells — 6 rows */}
      <div className="grid grid-cols-7 bg-white dark:bg-gray-800">
        {grid.map((cell, i) => {
          if (!cell.isCurrentMonth) {
            return (
              <div
                key={`outside-${i}`}
                className="aspect-square flex flex-col items-center justify-center border-b border-r border-gray-100 dark:border-gray-700/50 text-gray-300 dark:text-gray-600 text-sm"
              >
                {cell.day}
              </div>
            );
          }

          const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(cell.day).padStart(2, "0")}`;
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === toDateStr(selectedDate);
          const events = eventMap[dateStr] || [];

          const hasClass = events.some((e) => e.type === "class");
          const hasExam = events.some((e) => e.type === "exam");

          return (
            <button
              key={cell.day}
              onClick={() =>
                onSelectDate(new Date(currentYear, currentMonth, cell.day))
              }
              className={cn(
                "aspect-square flex flex-col items-center justify-center border-b border-r border-gray-100 dark:border-gray-700/50 transition-all duration-150 relative group",
                isSelected
                  ? "bg-accent1 text-white"
                  : isToday
                    ? "bg-accent1/10 dark:bg-accent1/20 text-accent1"
                    : "text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-750"
              )}
            >
              {/* Date number */}
              <span
                className={cn(
                  "text-sm font-bold leading-none",
                  isToday && !isSelected && "text-accent1 font-black"
                )}
              >
                {cell.day}
              </span>

              {/* Event dots */}
              {(hasClass || hasExam) && (
                <div className="flex items-center gap-0.5 mt-1">
                  {hasClass && (
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-sm",
                        isSelected ? "bg-white/80" : "bg-accent1"
                      )}
                    />
                  )}
                  {hasExam && (
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-sm",
                        isSelected ? "bg-white/80" : "bg-red-500"
                      )}
                    />
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DAY DETAIL PANEL
// ---------------------------------------------------------------------------

const EventCard = ({ event, index }) => {
  const isExam = event.type === "exam";
  const Icon = isExam ? AlertTriangle : GraduationCap;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.06 }}
      className={cn(
        "rounded-sm border overflow-hidden transition-all duration-200",
        isExam
          ? "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20"
          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-accent1/40"
      )}
    >
      {/* Top accent bar */}
      <div className={cn("h-1 w-full", isExam ? "bg-red-500" : "bg-accent1")} />

      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "h-9 w-9 rounded-sm flex items-center justify-center flex-shrink-0",
              isExam
                ? "bg-red-100 dark:bg-red-900/40"
                : "bg-accent1/10 dark:bg-accent1/20"
            )}
          >
            <Icon
              className={cn(
                "h-4 w-4",
                isExam ? "text-red-600 dark:text-red-400" : "text-accent1"
              )}
            />
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-gray-900 dark:text-white tracking-tight leading-snug">
              {event.title}
            </p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <Clock className="h-3 w-3 text-gray-400 dark:text-gray-500" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {event.time}
              </span>
            </div>
          </div>
        </div>

        {/* Course code tag */}
        {event.courseCode && (
          <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-700/50">
            <span className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
              {event.courseCode}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const DayDetailPanel = ({ selectedDate, events }) => {
  const sorted = useMemo(() => {
    const order = { exam: 0, class: 1 };
    return [...events].sort(
      (a, b) => (order[a.type] ?? 2) - (order[b.type] ?? 2)
    );
  }, [events]);

  return (
    <div>
      {/* Date header */}
      <div className="mb-5">
        <p className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1">
          Selected Day
        </p>
        <h3 className="text-xl font-black tracking-tight text-gray-900 dark:text-white">
          {formatSelectedDate(selectedDate)}
        </h3>
        <div className={cn(accentBarSection, "mt-2")} />
      </div>

      {/* Event count summary */}
      {sorted.length > 0 && (
        <div className="mb-4 px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-sm border border-gray-100 dark:border-gray-700/50">
          <span className="text-xs font-bold text-gray-600 dark:text-gray-300">
            {sorted.length} event{sorted.length > 1 ? "s" : ""} scheduled
          </span>
        </div>
      )}

      {/* Event list */}
      <AnimatePresence mode="wait">
        {sorted.length > 0 ? (
          <motion.div
            key={toDateStr(selectedDate)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
          >
            {sorted.map((event, i) => (
              <EventCard
                key={`${event.type}-${event.courseCode}-${i}`}
                event={event}
                index={i}
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-sm p-8 text-center"
          >
            <Calendar className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
              No Events Scheduled
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Select a highlighted date to see classes or exams.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const EventPlanner = ({ courses = [] }) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(today);

  const eventMap = useEventMap(courses);

  const todayEvents = useMemo(
    () => eventMap[toDateStr(today)] || [],
    [eventMap]
  );

  const selectedDateEvents = useMemo(
    () => eventMap[toDateStr(selectedDate)] || [],
    [eventMap, selectedDate]
  );

  const handlePrevMonth = useCallback(() => {
    setCurrentMonth((m) => {
      if (m === 0) {
        setCurrentYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth((m) => {
      if (m === 11) {
        setCurrentYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const handleToday = useCallback(() => {
    const now = new Date();
    setCurrentMonth(now.getMonth());
    setCurrentYear(now.getFullYear());
    setSelectedDate(now);
  }, []);

  return (
    <div className="space-y-5">
      {/* Today At A Glance */}
      <TodayAtAGlance todayEvents={todayEvents} />

      {/* Upcoming Classes Strip */}
      <UpcomingClassesStrip eventMap={eventMap} />

      {/* Calendar + Day Detail */}
      <motion.div {...motionStaggerChild(2, 0.1)}>
        <div className="mb-4">
          <h3 className="text-base font-bold text-gray-900 dark:text-white tracking-tight">
            Schedule Calendar
          </h3>
          <div className={cn(accentBarSection, "mt-1.5 !w-8")} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Left — Calendar */}
          <div className={cn("lg:col-span-7", card, "p-4 sm:p-5")}>
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-black tracking-tight text-gray-900 dark:text-white">
                {MONTH_NAMES[currentMonth]} {currentYear}
              </h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleToday}
                  className="px-2.5 py-1 mr-1 rounded-sm text-xs font-bold uppercase tracking-wider text-accent1 bg-accent1/10 hover:bg-accent1/20 transition-colors"
                >
                  Today
                </button>
                <button
                  onClick={handlePrevMonth}
                  className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </button>
                <button
                  onClick={handleNextMonth}
                  className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <CalendarGrid
              currentMonth={currentMonth}
              currentYear={currentYear}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              eventMap={eventMap}
            />

            {/* Legend */}
            <div className="flex items-center gap-6 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 bg-accent1 rounded-sm" />
                <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                  Class
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 bg-red-500 rounded-sm" />
                <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                  Exam
                </span>
              </div>
            </div>
          </div>

          {/* Right — Day Detail */}
          <div className={cn("lg:col-span-5", card, "p-4 sm:p-5")}>
            <DayDetailPanel
              selectedDate={selectedDate}
              events={selectedDateEvents}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EventPlanner;
